/**
 * Who may open the Access page, and the Graph client it uses.
 *
 * The owner check runs once per session, in the background: first ownership, with
 * `User.Read` only (already consented), then a silent token for the page's own Graph
 * permissions. The tab appears only when both pass, so nobody sees it until IT has
 * granted admin consent, and then it switches on for owners with no redeploy. Showing
 * owners a tab that can only say "waiting for IT" was noise. It is a courtesy, not
 * the control: Graph refuses a non-owner's membership change whatever the UI shows.
 *
 * In local development (no sign-in) the backend is a mock, chosen with
 * `?mockGraph=owner|consent|norole|notowner` on any URL. It is loaded by dynamic import
 * behind import.meta.env.DEV, so a production build does not contain it.
 */
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { InteractionRequiredAuthError, BrowserAuthError } from '@azure/msal-browser'
import { useAuth, isDevBypass } from '@/auth/AuthProvider'
import { TOKEN_HEADER } from '@/data/client'
import { apiFetch } from '@/auth/apiFetch'
import { viewingAs } from '@/data/viewAs'
import {
  createGraph,
  isConsentPending,
  MANAGE_SCOPES,
  type DepartmentGrant,
  type GraphClient,
  type GroupKey,
} from './graph'

export type Authorize = 'ok' | 'consent' | 'interaction'
export type GrantsResult = { ok: true; grants: DepartmentGrant[] } | { ok: false; status: number }

export interface AccessBackend {
  graph: GraphClient
  /** Gets the page's Graph permissions. `interactive` only from a click. */
  authorize(interactive: boolean): Promise<Authorize>
  grants(emails: string[]): Promise<GrantsResult>
}

type Owner = 'checking' | 'owner' | 'not-owner'

interface AccessState {
  owner: Owner
  owns: GroupKey[]
  backend: AccessBackend | null
}

const Ctx = createContext<AccessState>({ owner: 'checking', owns: [], backend: null })

export const useAccess = () => useContext(Ctx)

function realBackend(
  graphToken: (s: string[], i?: boolean) => Promise<string>,
  idToken: string | null,
): AccessBackend {
  return {
    graph: createGraph((scopes) => graphToken(scopes)),
    async authorize(interactive) {
      try {
        await graphToken(MANAGE_SCOPES, interactive)
        return 'ok'
      } catch (e) {
        if (isConsentPending(e)) return 'consent'
        if (e instanceof InteractionRequiredAuthError) return 'interaction'
        // The person closed the popup. Offer the button again rather than an error.
        if (e instanceof BrowserAuthError && e.errorCode === 'user_cancelled') return 'interaction'
        throw e
      }
    },
    async grants(emails) {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' }
      if (idToken) headers[TOKEN_HEADER] = idToken
      const res = await apiFetch('/api/access', {
        method: 'POST',
        headers,
        body: JSON.stringify({ emails }),
      })
      if (!res.ok) return { ok: false, status: res.status }
      const body = (await res.json()) as { grants: DepartmentGrant[] }
      return { ok: true, grants: body.grants }
    },
  }
}

const MOCK_KEY = 'cpc.mockGraph'

function mockScenario(): string | null {
  if (!import.meta.env.DEV || !isDevBypass) return null
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('mockGraph')
    if (fromUrl) sessionStorage.setItem(MOCK_KEY, fromUrl)
    return fromUrl ?? sessionStorage.getItem(MOCK_KEY)
  } catch {
    return null
  }
}

export function AccessProvider({ children }: { children: ReactNode }) {
  const { account, token, graphToken } = useAuth()
  const [mock, setMock] = useState<AccessBackend | null>(null)
  const scenario = useMemo(mockScenario, [])

  useEffect(() => {
    // The DEV test is repeated here, not only in mockScenario, so the bundler can see
    // this import is dead in a production build and leave the mock chunk out entirely.
    if (import.meta.env.DEV && scenario) {
      void import('./mock').then((m) => setMock(m.mockBackend(scenario)))
    }
  }, [scenario])

  const backend = useMemo<AccessBackend | null>(() => {
    // No Access page while viewing as someone: it changes group membership, and View
    // as is read-only. With no backend the tab does not appear.
    if (viewingAs()) return null
    if (scenario) return mock
    if (!account) return null
    return realBackend(graphToken, token)
    // graphToken is rebuilt every render but closes over nothing beyond the account,
    // so the account is the dependency; listing graphToken would rebuild on every render.
  }, [scenario, mock, account, token])

  const [owns, setOwns] = useState<GroupKey[] | null>(null)

  useEffect(() => {
    if (!backend) {
      setOwns(scenario ? null : [])
      return
    }
    let cancelled = false
    backend.graph
      .ownedGroups()
      // An owner still sees nothing until IT has consented to the page's permissions.
      // 'interaction' means consent exists and only a click-driven popup is needed, so
      // that counts as ready; 'consent' (or any failure) hides the tab.
      .then(async (k) => {
        if (k.length === 0 || scenario) return k
        const auth = await backend.authorize(false).catch(() => 'consent' as const)
        return auth === 'consent' ? [] : k
      })
      .then((k) => !cancelled && setOwns(k))
      // Anything going wrong here means "no tab". It is not worth an error on every page.
      .catch(() => !cancelled && setOwns([]))
    return () => {
      cancelled = true
    }
  }, [backend, scenario])

  const value: AccessState = {
    owner: owns === null ? 'checking' : owns.length > 0 ? 'owner' : 'not-owner',
    owns: owns ?? [],
    backend,
  }
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
